import type { VideoCodec } from 'livekit-client';

/**
 * Выбор видеокодека.
 *
 * Кодек нельзя поменять «на лету» — он фиксируется в момент публикации
 * дорожки, и смена означает перепубликацию, то есть заметный обрыв
 * картинки у всех участников. Поэтому кодек выбирается ровно один раз,
 * при входе: берётся надёжный вариант из тех, что браузер действительно
 * умеет кодировать (узнаём через `RTCRtpSender.getCapabilities`).
 *
 * Подстройка под связь во время встречи — не дело кодека. Этим заняты
 * simulcast, dynacast и встроенный в WebRTC контроль перегрузки: они
 * меняют слой и битрейт непрерывно, плавно и в обе стороны.
 *
 * Статистика ниже (`sampleLink`) снимается только для показа в панели
 * качества — решений по ней никто не принимает.
 */

export type CodecPreference = 'auto' | VideoCodec;

/**
 * Лестница качества: чем раньше в списке, тем лучше сжатие при том же
 * битрейте — и тем дороже кодирование по процессору.
 */
const LADDER: VideoCodec[] = ['av1', 'vp9', 'h264', 'vp8'];

/** Кодек по умолчанию, если он доступен. */
const AUTO_START: VideoCodec = 'vp9';

export const CODEC_LABEL: Record<VideoCodec, string> = {
  av1: 'AV1',
  vp9: 'VP9',
  h264: 'H.264',
  vp8: 'VP8',
  h265: 'H.265',
};

export const CODEC_NOTE: Record<VideoCodec, string> = {
  av1: 'Лучшее сжатие, самый требовательный к процессору',
  vp9: 'Отличное качество, аппаратная поддержка почти везде',
  h264: 'Максимальная совместимость, выше битрейт',
  vp8: 'Запасной вариант для старых клиентов',
  h265: 'Редко поддерживается в браузерах',
};

// ── Что умеет этот браузер ────────────────────────────────────────────

/**
 * Кодеки, которые браузер может именно **кодировать**.
 * Список приёма шире списка отправки, поэтому спрашиваем про sender.
 */
export function detectSupportedCodecs(): VideoCodec[] {
  if (typeof RTCRtpSender === 'undefined' || !RTCRtpSender.getCapabilities) {
    // Без Capabilities API судить не о чем — считаем доступным только базовый.
    return ['h264', 'vp8'];
  }

  const capabilities = RTCRtpSender.getCapabilities('video');
  if (!capabilities) return ['h264', 'vp8'];

  const mimeTypes = new Set(capabilities.codecs.map((c) => c.mimeType.toLowerCase()));
  const supported = LADDER.filter((codec) => mimeTypes.has(`video/${codec}`));

  // Хоть что-то отправлять браузер обязан уметь.
  return supported.length > 0 ? supported : ['vp8'];
}

/**
 * Safari (и все браузеры на iOS, включая webview Telegram — там всегда
 * движок WebKit) плохо работает с SVC-кодеками: livekit-client сам обходит
 * для них часть логики слоёв, а на практике VP9/AV1 дают там артефакты
 * и повышенный расход батареи. H.264 на этих устройствах кодируется
 * аппаратно и работает предсказуемо.
 */
export function prefersHardwareH264(
  ua: string | undefined = typeof navigator === 'undefined' ? undefined : navigator.userAgent,
  touchPoints: number = typeof navigator === 'undefined' ? 0 : (navigator.maxTouchPoints ?? 0),
): boolean {
  if (!ua) return false;

  // iPadOS выдаёт себя за macOS — отличается наличием тачскрина.
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);

  // Chrome и Firefox на iOS тоже содержат «Safari» в строке, но они
  // всё равно работают на WebKit, так что вывод для них тот же.
  const isSafari = /safari/i.test(ua) && !/chrome|chromium|android|edg\//i.test(ua);

  return isIOS || isSafari;
}

/**
 * Кодек для автоматического режима.
 *
 * Берём VP9: он даёт хорошее сжатие, поддержан аппаратно почти везде и,
 * в отличие от AV1, не упирается в процессор на обычных рабочих машинах.
 * Угадывать что-то по Network Information API бессмысленно — его нет в
 * половине браузеров и он неточен в остальных.
 */
export function pickInitialCodec(supported: VideoCodec[]): VideoCodec {
  if (prefersHardwareH264() && supported.includes('h264')) return 'h264';
  if (supported.includes(AUTO_START)) return AUTO_START;
  return LADDER.find((codec) => supported.includes(codec)) ?? 'vp8';
}

/** Разрешает пользовательский выбор в конкретный кодек. */
export function resolveCodec(preference: CodecPreference, supported: VideoCodec[]): VideoCodec {
  if (preference === 'auto') return pickInitialCodec(supported);
  if (supported.includes(preference)) return preference;
  // Выбранный вручную кодек недоступен — тихо откатываемся, а не падаем.
  return pickInitialCodec(supported);
}

// ── Статистика WebRTC для панели качества ─────────────────────────────

export interface LinkSample {
  /** Что ограничивает качество прямо сейчас, по мнению самого WebRTC. */
  limitation: 'none' | 'cpu' | 'bandwidth' | 'other';
  /** Доля потерянных пакетов по отчёту получателя, 0..1. */
  loss: number;
  /** Круговая задержка, секунды. */
  rtt: number;
  /** Оценка доступной исходящей полосы, бит/с. */
  bitrate: number;
  /** Фактическая частота кадров отправки. */
  fps: number;
  /** Размер кадра, который уходит в сеть прямо сейчас. */
  width: number;
  height: number;
  /** Сколько бит в секунду реально отправляется. */
  sendBitrate: number;
  /**
   * Идёт ли передача видео прямо сейчас.
   *
   * Ложно — это не поломка. Пока на вас никто не подписан, сервер просит
   * не тратить канал впустую (dynacast), кодировщик стоит, и размеров
   * кадра в статистике просто нет. Интерфейсу нужно сказать об этом
   * словами, а не показывать «н/д».
   */
  sending: boolean;
  /** Есть ли уже с чем сравнить, чтобы посчитать битрейт. */
  measured: boolean;
}

/**
 * Предыдущий отсчёт отправленных байт — по нему считается фактический
 * битрейт. Слабые ссылки: отправитель уходит вместе с дорожкой, и держать
 * его здесь после этого незачем.
 */
const previousBytes = new WeakMap<RTCRtpSender, { bytes: number; at: number }>();

/**
 * Насколько старым может быть предыдущий отсчёт, чтобы по нему считать.
 *
 * Пока панель качества закрыта, пробы не снимаются, и в карте остаётся
 * запись многоминутной давности. Разделив на неё, мы получили бы средний
 * битрейт за всё это время — правдоподобное, но неверное число. Лучше
 * честно сказать «измеряем…» и выдать настоящее через секунду.
 */
const MAX_SAMPLE_GAP_MS = 10_000;

/**
 * Задержка выше этого значения — заведомо не измерение, а мусор.
 * Пять секунд круговой задержки означали бы, что связи нет вовсе.
 */
const MAX_PLAUSIBLE_RTT = 5;

const plausibleRtt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value < MAX_PLAUSIBLE_RTT;

/** Снимает показатели исходящего видеопотока. */
export async function sampleLink(sender: RTCRtpSender): Promise<LinkSample | null> {
  let report: RTCStatsReport;
  try {
    report = await sender.getStats();
  } catch {
    return null;
  }

  const sample: LinkSample = {
    limitation: 'none',
    loss: 0,
    rtt: 0,
    bitrate: 0,
    fps: 0,
    width: 0,
    height: 0,
    sendBitrate: 0,
    sending: false,
    measured: false,
  };
  let seenOutbound = false;
  let bytesSent = 0;
  let pairRtt = 0;
  let reportedRtt = 0;

  report.forEach((stat: any) => {
    if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
      seenOutbound = true;
      if (typeof stat.qualityLimitationReason === 'string') {
        sample.limitation = stat.qualityLimitationReason;
      }
      if (typeof stat.framesPerSecond === 'number') {
        // При simulcast/SVC слоёв несколько — берём максимальный.
        sample.fps = Math.max(sample.fps, stat.framesPerSecond);
      }
      // Размер самого крупного слоя, который действительно кодируется:
      // именно он показывает, до чего опустился или поднялся отправитель.
      if (typeof stat.frameWidth === 'number' && stat.frameWidth > sample.width) {
        sample.width = stat.frameWidth;
        sample.height = stat.frameHeight ?? 0;
      }
      if (typeof stat.bytesSent === 'number') bytesSent += stat.bytesSent;
    }

    if (stat.type === 'remote-inbound-rtp' && stat.kind === 'video') {
      if (typeof stat.fractionLost === 'number') {
        sample.loss = Math.max(sample.loss, stat.fractionLost);
      }
      // Запасной источник задержки — см. оговорку про правдоподобие ниже.
      if (plausibleRtt(stat.roundTripTime)) {
        reportedRtt = Math.max(reportedRtt, stat.roundTripTime);
      }
    }

    if (stat.type === 'candidate-pair' && (stat.nominated || stat.state === 'succeeded')) {
      if (typeof stat.availableOutgoingBitrate === 'number') {
        sample.bitrate = Math.max(sample.bitrate, stat.availableOutgoingBitrate);
      }
      if (plausibleRtt(stat.currentRoundTripTime)) {
        pairRtt = Math.max(pairRtt, stat.currentRoundTripTime);
      }
    }
  });

  /*
   * Задержку берём с ICE-пары, а не из отчёта получателя.
   *
   * `remote-inbound-rtp.roundTripTime` считается по отметкам времени в
   * RTCP, и пока обратных отчётов пришло мало, там встречается мусор —
   * в панели это выглядело как «задержка 3 973 829 мс». Значения
   * фильтруются по правдоподобию, а первым источником идёт ICE-пара:
   * она измеряет задержку напрямую и врёт заметно реже.
   */
  sample.rtt = pairRtt || reportedRtt;

  if (!seenOutbound) return null;

  const now = Date.now();
  const previous = previousBytes.get(sender);
  previousBytes.set(sender, { bytes: bytesSent, at: now });

  const gap = previous ? now - previous.at : Infinity;
  if (previous && gap > 0 && gap <= MAX_SAMPLE_GAP_MS && bytesSent >= previous.bytes) {
    sample.measured = true;
    sample.sendBitrate = ((bytesSent - previous.bytes) * 8000) / gap;
  }

  // Кадры уходят — значит, кодировщик работает. Размер кадра появляется
  // в статистике только вместе с передачей, поэтому проверяем его же.
  sample.sending = sample.width > 0 && (!sample.measured || sample.sendBitrate > 0);

  return sample;
}
